import { Client, StreamableHTTPClientTransport } from '@modelcontextprotocol/client';
import type { ToolClient, ToolInfo, ToolOutcome } from './types.ts';

/**
 * MCP client used by the simulated Alexa+ host.
 *
 * It connects over Streamable HTTP exactly as Alexa+ does (2025-11-25
 * `initialize` handshake by default, or 2026-07-28 via `era: 'auto'`),
 * declares form + URL elicitation so the server can ask the customer to
 * confirm on screen, and records every JSON-RPC exchange for the
 * "protocol receipts" panel in the simulator.
 */

export type ElicitationRequest = {
  mode: 'form' | 'url';
  message: string;
  requestedSchema?: Record<string, unknown>;
  url?: string;
};

export type ElicitationAnswer = { action: 'accept' | 'decline' | 'cancel'; content?: Record<string, unknown> };

export type Receipt = { at: string; direction: 'out' | 'in'; method: string; summary: string; ms?: number };

export type McpToolClientOptions = {
  url: string;
  headers?: Record<string, string>;
  era?: 'legacy' | 'auto';
  clientName?: string;
  onElicit?: (req: ElicitationRequest) => Promise<ElicitationAnswer>;
  onReceipt?: (r: Receipt) => void;
};

export class McpToolClient implements ToolClient {
  readonly #opts: McpToolClientOptions;
  #client?: Client;
  #tools?: ToolInfo[];
  #connecting?: Promise<Client>;

  constructor(opts: McpToolClientOptions) {
    this.#opts = opts;
  }

  get protocolEra(): string | undefined {
    return this.#client?.getProtocolEra?.();
  }

  get serverVersion() {
    return this.#client?.getServerVersion?.();
  }

  async #connect(): Promise<Client> {
    if (this.#client) return this.#client;
    this.#connecting ??= (async () => {
      const client = new Client(
        { name: this.#opts.clientName ?? 'Alexa+ Simulator (Rally)', version: '1.0.0' },
        {
          // Only claim elicitation when someone can actually answer it.
          capabilities: this.#opts.onElicit ? { elicitation: { form: {}, url: {} } } : {},
          ...(this.#opts.era === 'auto' ? { versionNegotiation: { mode: 'auto' as const } } : {}),
        },
      );
      if (this.#opts.onElicit) client.setRequestHandler('elicitation/create', async (request) => {
        const p = request.params as { mode?: 'form' | 'url'; message: string; requestedSchema?: Record<string, unknown>; url?: string };
        this.#receipt('in', 'elicitation/create', p.message);
        if (!this.#opts.onElicit) return { action: 'decline' as const };
        const asked = performance.now();
        const answer = await this.#opts.onElicit({ mode: p.mode ?? 'form', message: p.message, requestedSchema: p.requestedSchema, url: p.url });
        this.#waitingMs += performance.now() - asked;
        this.#receipt('out', 'elicitation/response', answer.action);
        return answer as never;
      });
      const transport = new StreamableHTTPClientTransport(new URL(this.#opts.url), {
        requestInit: { headers: this.#opts.headers ?? {} },
      });
      const started = performance.now();
      await client.connect(transport);
      this.#receipt('in', this.#opts.era === 'auto' ? 'server/discover' : 'initialize', `${client.getServerVersion()?.name ?? 'server'} (${client.getProtocolEra?.() ?? 'legacy'} era)`, started);
      this.#client = client;
      return client;
    })();
    return this.#connecting;
  }

  declare private receiptOf: (direction: Receipt['direction'], method: string, summary: string, started?: number, waited?: number) => void;

  /** Time spent waiting for a person to answer a confirmation, which isn't the server's latency. */
  #waitingMs = 0;

  #receipt(direction: Receipt['direction'], method: string, summary: string, started?: number, waited = 0) {
    this.#opts.onReceipt?.({
      at: new Date().toISOString(),
      direction,
      method,
      summary: summary.slice(0, 240),
      ms: started ? Math.max(0, Math.round(performance.now() - started - waited)) : undefined,
    });
  }

  async listTools(): Promise<ToolInfo[]> {
    if (this.#tools) return this.#tools;
    const client = await this.#connect();
    const started = performance.now();
    const res = await client.listTools();
    this.#receipt('in', 'tools/list', `${res.tools.length} tools`, started);
    this.#tools = res.tools.map((t) => ({
      name: t.name,
      description: t.description ?? '',
      inputSchema: t.inputSchema as Record<string, unknown>,
      annotations: t.annotations,
      uiResourceUri: ((t._meta as { ui?: { resourceUri?: string } } | undefined)?.ui?.resourceUri) ?? undefined,
    }));
    return this.#tools;
  }

  async callTool(name: string, args: Record<string, unknown>, opts: { quiet?: boolean } = {}): Promise<ToolOutcome> {
    const client = await this.#connect();
    const started = performance.now();
    const waitedBefore = this.#waitingMs;
    const receipt: McpToolClient['receiptOf'] = opts.quiet ? () => {} : (d, m, s, t, w) => this.#receipt(d, m, s, t, w);
    receipt('out', 'tools/call', `${name} ${JSON.stringify(args)}`);
    try {
      const res = await client.callTool({ name, arguments: args });
      // Latency is the server's: time the person spent answering a confirmation is reported separately.
      const waited = opts.quiet ? 0 : this.#waitingMs - waitedBefore;
      const latencyMs = Math.max(0, Math.round(performance.now() - started - waited));
      const text = (res.content as Array<{ type: string; text?: string }> | undefined)?.filter((c) => c.type === 'text').map((c) => c.text).join('\n') ?? '';
      const data = res.structuredContent as Record<string, unknown> | undefined;
      const ui = (res._meta as { ui?: { resourceUri?: string } } | undefined)?.ui?.resourceUri;
      receipt('in', 'tools/call result', `${name}${res.isError ? ' (error)' : ''} ${typeof data?.speech === 'string' ? `“${data.speech}”` : text.slice(0, 120)}${waited >= 50 ? ` (plus ${(waited / 1000).toFixed(1)} s waiting for a yes)` : ''}`, started, waited);
      return { isError: Boolean(res.isError), data, text, uiResourceUri: ui, latencyMs };
    } catch (err) {
      const latencyMs = Math.round(performance.now() - started);
      receipt('in', 'tools/call error', `${name}: ${(err as Error).message}`, started);
      return { isError: true, text: (err as Error).message, latencyMs };
    }
  }

  async readResource(uri: string): Promise<{ text: string; mimeType?: string; meta?: Record<string, unknown> }> {
    const client = await this.#connect();
    const started = performance.now();
    const res = await client.readResource({ uri });
    const c = res.contents[0] as { text?: string; mimeType?: string; _meta?: Record<string, unknown> } | undefined;
    this.#receipt('in', 'resources/read', `${uri} (${c?.text?.length ?? 0} bytes)`, started);
    return { text: c?.text ?? '', mimeType: c?.mimeType, meta: c?._meta };
  }

  async close(): Promise<void> {
    await this.#client?.close();
    this.#client = undefined;
    this.#connecting = undefined;
  }
}
